/**
 * The note lane column type (W2-03 note-lane; contract `TimelineColumnDef`, ARCHITECTURE.md §7.7, DESIGN.md §4.6).
 *
 * Instances: the active line's lane (`lane:active`, stable across line switches so its width is kept) and one lane
 * per pinned line (`lane:<id>`, editor store `pinnedLanes`). Each instance gets a fresh `LaneView` when it
 * (re)appears (the host owns and disposes views). The view's model reads the document, the editor store, the engine
 * and the session live, so switching lines, selecting, hovering or zooming only needs a redraw (the Chart timeline
 * panel invalidates on store changes); a change of pinned lanes needs a relayout.
 */

import type { LineId, NoteId } from '../../core/model/types';
import type { AppServices } from '../../app/registry/types';
import type { TimelineColumnDef, TimelineColumnInstance } from '../../render/types';
import {
  ACTIVE_LANE_ID,
  createLaneView,
  EMPTY_OVERLAY,
  LANE_DEF_ID,
  LANE_ORDER,
  LANE_WIDTH,
  pinnedLaneId,
  type LaneOverlayModel,
  type LaneView,
  type LaneViewModel,
} from '../../render/timeline/laneColumn';
import { FLAG_BELOW, type LaneNoteIndex } from '../../render/timeline/laneHit';
import { createLaneHandlers, type LaneGestureController } from './laneGestures';
import { m } from './noteLane.messages';
import {
  commandKeysText,
  laneSession,
  noteIndexOf,
  playheadSec,
  ref,
  type LaneSession,
  type LaneSides,
} from './laneSession';
import { pasteOverlay } from './pasteFlow';
import { placementGhost } from './placement';
import { boxModel, noteBox } from './transformBox';
import { laneHeaderFor } from './LaneHeader';

/** Whether a note with these index flags shows with the sides filter. */
export function sidesInclude(sides: LaneSides, flags: number): boolean {
  if (sides === 'both') return true;
  const below = (flags & FLAG_BELOW) !== 0;
  return sides === 'below' ? below : !below;
}

const handlersBySession = new WeakMap<LaneSession, LaneGestureController>();

/** The session's gesture controller (one for every lane of a services object). */
export function laneHandlers(services: AppServices): LaneGestureController {
  const s = laneSession(services);
  let h = handlersBySession.get(s);
  if (h === undefined) {
    h = createLaneHandlers(services);
    handlersBySession.set(s, h);
  }
  return h;
}

/** Translated empty-state lines of a lane, or null. */
export function laneMessage(
  services: AppServices,
  line: LineId | null,
  index: LaneNoteIndex,
): readonly string[] | null {
  const store = services.store;
  if (store === null) return null;
  const t = (key: string, params?: Readonly<Record<string, string | number>>): string => services.i18n.t(key, params);
  if (line === null || !store.doc.lines.has(line)) return [t(m.noLineTitle), t(m.noLineBody)];
  const rec = store.doc.lines.get(line);
  if (rec !== undefined && rec.attachUI !== null) {
    return [t(m.uiMoverTitle, { element: t(m.uiElement, { element: rec.attachUI }) }), t(m.uiMoverBody)];
  }
  if (index.count > 0) return null;
  if (services.editor.getState().filters.lockedLines.has(line)) return [t(m.lockedTitle), t(m.lockedBody)];
  // Shortcut labels come from the keymap (rebindings, macOS labels); 'none' when a command has no key.
  return [
    t(m.emptyLaneTitle),
    t(m.emptyLaneBody, {
      tapTool: commandKeysText(services, 'tools.tap'),
      placeTap: commandKeysText(services, 'notes.placeTap'),
    }),
  ];
}

/** The live model of one lane instance. */
export function laneModel(services: AppServices, lineOf: () => LineId | null, isActiveLane: boolean): LaneViewModel {
  const s = laneSession(services);
  const notes = (): LaneNoteIndex => noteIndexOf(services.store, lineOf());
  return {
    line: () => {
      const line = lineOf();
      return line !== null && services.store?.doc.lines.has(line) === true ? line : null;
    },
    notes,
    ghosts() {
      if (!isActiveLane) return [];
      const own = lineOf();
      const store = services.store;
      if (store === null) return [];
      return services.editor
        .getState()
        .alsoShow.filter((l) => l !== own && store.doc.lines.has(l))
        .map((l) => noteIndexOf(store, l));
    },
    noteState(i, id: NoteId) {
      const sel = services.editor.getState().selection;
      let mh = false;
      const engine = services.engine;
      if (engine !== null) {
        try {
          mh = engine.isMultiHighlight(id);
        } catch {
          mh = false;
        }
      }
      return { selected: sel.notes.has(id), mh, filtered: !sidesInclude(s.sides, notes().flags[i] ?? 0) };
    },
    include: (i) => sidesInclude(s.sides, notes().flags[i] ?? 0),
    xView() {
      const tl = services.editor.getState().timeline;
      return { center: tl.laneCenterX, half: tl.laneHalfRange };
    },
    setXView(v) {
      services.editor.getState().setTimeline({ laneCenterX: v.center, laneHalfRange: v.half });
    },
    grid() {
      const e = services.editor.getState();
      return { snapDiv: e.snap.div, xGrid: e.snap.xGrid, xSnap: e.snap.xSnap, waveform: e.timeline.waveform };
    },
    locked() {
      const line = lineOf();
      return line !== null && services.editor.getState().filters.lockedLines.has(line);
    },
    message: () => laneMessage(services, lineOf(), notes()),
    overlay(): LaneOverlayModel {
      const line = lineOf();
      if (services.store === null || line === null) return EMPTY_OVERLAY;
      const e = services.editor.getState();
      const hover = e.hover;
      let box = null;
      if (e.tool === 'select' && e.placement === null && s.gesture.move === null && e.selection.notes.size >= 2) {
        const store = services.store;
        const nb = noteBox(notes(), e.selection.notes, (id) => {
          const n = store.doc.notes.get(id);
          return n === undefined ? null : { t: n.t, end: n.end };
        });
        if (nb !== null) box = boxModel(nb, s.gesture.boxHandle);
      }
      return {
        placement: placementGhost(services, line),
        paste: pasteOverlay(services, line),
        move: s.gesture.move,
        box,
        eraser: s.gesture.eraser,
        lanePoint: hover?.kind === 'lanePoint' && hover.line === line ? { beat: hover.beat, x: hover.x } : null,
        hover: hover?.kind === 'note' ? hover.note : null,
      };
    },
    plusXAngle() {
      const line = lineOf();
      const engine = services.engine;
      if (line === null || engine === null) return null;
      try {
        const st = engine.lineState(line, playheadSec(services));
        return Number.isFinite(st.rot) ? (st.rot * Math.PI) / 180 : null;
      } catch {
        return null;
      }
    },
    emptyCursor() {
      const tool = services.editor.getState().tool;
      if (tool === 'eraser') return 'cell';
      if (tool === 'select') return 'default';
      return 'crosshair';
    },
    slop: () => 0,
  };
}

/** The lane column def of a services object. */
export function laneColumnDef(services: AppServices): TimelineColumnDef {
  const s = laneSession(services);
  const instances = new Map<string, TimelineColumnInstance>();
  const header = laneHeaderFor(services);

  function makeInstance(id: string, lineOf: () => LineId | null, active: boolean): TimelineColumnInstance {
    const view: LaneView = createLaneView({
      instanceId: id,
      model: laneModel(services, lineOf, active),
      handlers: laneHandlers(services),
      onDispose: (v) => {
        if (s.views.get(id) === v) s.views.delete(id);
        if (instances.get(id)?.view === v) instances.delete(id);
        if (s.pointer?.instanceId === id) s.pointer = null;
      },
    });
    s.views.set(id, view);
    return { id, defId: LANE_DEF_ID, widthPx: LANE_WIDTH.default, header, view, region: 'lane' };
  }

  return {
    id: LANE_DEF_ID,
    title: ref(m.laneColumn),
    order: LANE_ORDER,
    width: LANE_WIDTH,
    instances() {
      const store = services.store;
      const e = services.editor.getState();
      const wanted: Array<{ id: string; line: LineId | null; active: boolean }> = [
        { id: ACTIVE_LANE_ID, line: null, active: true },
      ];
      if (store !== null) {
        for (const line of e.pinnedLanes) {
          if (!store.doc.lines.has(line) || line === e.activeLine) continue;
          wanted.push({ id: pinnedLaneId(line), line, active: false });
        }
      }
      // While a motion column is expanded the lane asks for its narrowest width, so the curve editor gets the room
      // (UX-039); a width the user dragged still wins (the host keeps user widths).
      const widthPx = e.timeline.expandedColumn !== null ? LANE_WIDTH.min : LANE_WIDTH.default;
      const out: TimelineColumnInstance[] = [];
      for (const w of wanted) {
        let inst = instances.get(w.id);
        if (inst === undefined || (inst.view as LaneView).disposed) {
          const fixed = w.line;
          inst = makeInstance(w.id, w.active ? () => services.editor.getState().activeLine : () => fixed, w.active);
          instances.set(w.id, inst);
        }
        if (inst.widthPx !== widthPx) {
          inst = { ...inst, widthPx };
          instances.set(w.id, inst);
        }
        out.push(inst);
      }
      for (const id of [...instances.keys()]) if (!wanted.some((w) => w.id === id)) instances.delete(id);
      return out;
    },
  };
}
